/**
 * Notification channels API client.
 *
 * Mirrors `backend/src/hub/routes/channels.py`. Channels are
 * destinations for notifications: in_app (bell), email (SMTP),
 * telegram. Alert rules pick which channels to fire through.
 */
import { api } from "./api";


export type ChannelType = "in_app" | "email" | "telegram";


export interface Channel {
  id: string;
  name: string;
  description: string | null;
  type: ChannelType;
  config: Record<string, unknown>;
  credential_id: string | null;
  enabled: boolean;
  tags: string[];
  last_used_at: string | null;
  last_error: string | null;
  created_by: string;
  created_at: string;
  updated_at: string | null;
  /** True for the in_app singleton — cannot be deleted. */
  is_singleton: boolean;
}


export interface ChannelCreate {
  type: ChannelType;
  name: string;
  description?: string | null;
  config?: Record<string, unknown>;
  credential_id?: string | null;
  enabled?: boolean;
  tags?: string[];
}


export type ChannelUpdate = Partial<Omit<ChannelCreate, "type">>;


export interface ChannelTypeInfo {
  type_id: ChannelType;
  label: string;
  description: string;
  config_schema: {
    properties?: Record<string, Record<string, unknown>>;
    required?: string[];
    [k: string]: unknown;
  };
  /** Which credential type this channel requires, or null. */
  credential_type: string | null;
  is_singleton: boolean;
}


export interface ChannelTestResult {
  ok: boolean;
  message: string;
}


export const channelsApi = {
  list: (params?: { type?: ChannelType; enabled?: boolean; tag?: string[] }) =>
    api.get<Channel[]>("/channels", { query: params }),

  types: () => api.get<ChannelTypeInfo[]>("/channels/types"),

  get: (id: string) => api.get<Channel>(`/channels/${id}`),

  create: (body: ChannelCreate) => api.post<Channel>("/channels", body),

  update: (id: string, body: ChannelUpdate) =>
    api.patch<Channel>(`/channels/${id}`, body),

  remove: (id: string) => api.delete<void>(`/channels/${id}`),

  test: (id: string) => api.post<ChannelTestResult>(`/channels/${id}/test`),
};
